// =============================================================================
// Admin Cost Manager Page — per-template cost overview + drill-in
// -----------------------------------------------------------------------------
// Cost editing for individual blank-variants lives in the per-template
// CatalogVariantMatrix (a much richer surface). This page is the
// operator's "cost-at-a-glance" entry point: it lists every template
// with the lowest variant cost + variant count, flags templates with
// missing costs (cost = 0), and links to the per-template editor for
// the actual edits.
//
// Backed by the existing `GET /admin/catalog/templates` endpoint — no
// new API route is needed.
// =============================================================================

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import { Button, Card, Spinner } from '@tkpod/ui';

interface TemplateSummary {
  id: string;
  name: string;
  slug: string;
  productType: string;
  status: string;
  thumbnailUrl: string | null;
  brand: string | null;
  variantCount: number;
  startingPrice: string | null;
  createdAt: string;
}

interface TemplatesResponse {
  data: TemplateSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function formatPrice(decimal: string | null): string {
  if (decimal === null) return '—';
  const num = Number.parseFloat(decimal);
  if (!Number.isFinite(num)) return decimal;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

export function AdminCostManagerPage() {
  const api = useApiClient();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const search = params.get('search') ?? '';
  const onlyMissing = params.get('missing') === 'true';

  const query = useQuery<TemplatesResponse>({
    queryKey: ['admin', 'cost-overview', { page, search }],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('limit', '25');
      qs.set('sortBy', 'name');
      qs.set('sortOrder', 'asc');
      if (search) qs.set('search', search);
      return api.get<TemplatesResponse>(`/api/v1/admin/catalog/templates?${qs}`);
    },
  });

  const items = useMemo(() => {
    if (!query.data) return [];
    if (!onlyMissing) return query.data.data;
    return query.data.data.filter(
      (t) => t.startingPrice === null || Number.parseFloat(t.startingPrice) === 0,
    );
  }, [query.data, onlyMissing]);

  function patch(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  return (
    <Card padding="none">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">
            Cost manager
          </h1>
          <p className="text-xs text-text-tertiary">
            Per-template wholesale cost overview. Use the variant matrix
            inside each template for fine-grained edits and bulk updates.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search template…"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                patch('search', (e.target as HTMLInputElement).value);
              }
            }}
            className="w-64 rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:border-brand-600 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) =>
                patch('missing', e.target.checked ? 'true' : null)
              }
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-600"
            />
            Only missing/zero cost
          </label>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : query.isError ? (
        <div className="space-y-2 px-5 py-6">
          <p className="text-sm text-rose-600">
            {query.error instanceof Error
              ? query.error.message
              : 'Unable to load templates.'}
          </p>
          <Button variant="secondary" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="px-5 py-12 text-center text-sm text-text-secondary">
          {onlyMissing
            ? 'No templates are missing variant costs — all variants are priced.'
            : 'No templates match your search.'}
        </p>
      ) : (
        <>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Template</th>
                <th className="px-5 py-2 font-semibold">Brand</th>
                <th className="px-5 py-2 font-semibold">Type</th>
                <th className="px-5 py-2 text-right font-semibold">
                  Variants
                </th>
                <th className="px-5 py-2 text-right font-semibold">
                  Lowest cost
                </th>
                <th className="px-5 py-2 font-semibold">Status</th>
                <th className="px-5 py-2 text-right font-semibold">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((tpl) => {
                const missing =
                  tpl.startingPrice === null ||
                  Number.parseFloat(tpl.startingPrice) === 0;
                return (
                  <tr
                    key={tpl.id}
                    className="border-b border-border/60 hover:bg-surface-tertiary/60"
                  >
                    <td className="px-5 py-3 align-top">
                      <Link
                        to={`/catalog/${tpl.id}`}
                        className="font-medium text-brand-600 hover:underline"
                      >
                        {tpl.name}
                      </Link>
                      <p className="text-xs text-text-tertiary">{tpl.slug}</p>
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tpl.brand ?? '—'}
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tpl.productType}
                    </td>
                    <td className="px-5 py-3 text-right align-top text-text-primary">
                      {tpl.variantCount}
                    </td>
                    <td
                      className={`px-5 py-3 text-right align-top font-medium ${
                        missing ? 'text-rose-600' : 'text-text-primary'
                      }`}
                    >
                      {formatPrice(tpl.startingPrice)}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                          tpl.status === 'PUBLISHED'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {tpl.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right align-top">
                      <Link
                        to={`/catalog/${tpl.id}`}
                        className="text-xs font-medium text-brand-600 hover:underline"
                      >
                        Edit costs →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-text-secondary">
            <span>
              Page {page} • {query.data?.pagination.total ?? 0} template
              {(query.data?.pagination.total ?? 0) === 1 ? '' : 's'} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => patch('page', String(page - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={
                  query.data
                    ? page * query.data.pagination.limit >=
                      query.data.pagination.total
                    : true
                }
                onClick={() => patch('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
